/**
 * Tiny singleton that throttles how many flagship iframes may be in their
 * "loading" phase at once. Prevents the Flagships section from saturating
 * the browser's connection pool when the visitor scrolls through all 8
 * cards — a freshly-scrolled card waits its turn instead of racing the
 * others for TTFB.
 *
 * A slot is acquired when the iframe is mounted and released when the
 * iframe signals `onLoad` (or is retried / unmounted).
 */

// One-at-a-time: with `min-h-screen` rows, only one card is in view, so there's
// no point in racing multiple iframes through TTFB. Serializing loads also cuts
// per-frame compositing cost during scroll (fewer live iframe layers at once).
const MAX_CONCURRENT = 1;

type Waiter = () => void;

let active = 0;
const waiters: Waiter[] = [];

export function acquireLoadSlot(onGranted: Waiter): () => void {
    if (active < MAX_CONCURRENT) {
        active++;
        onGranted();
        return () => {
            /* already granted — release is the caller's responsibility */
        };
    }
    waiters.push(onGranted);
    return () => {
        const idx = waiters.indexOf(onGranted);
        if (idx !== -1) waiters.splice(idx, 1);
    };
}

export function releaseLoadSlot(): void {
    active = Math.max(0, active - 1);
    const next = waiters.shift();
    if (next) {
        active++;
        next();
    }
}
